import { TeamEventContext } from '@/support/teamEvents/TeamEventContext';
import { type TeamEventOfType, type TeamEventType } from '@/model/TeamEvent.model';
import { useContext, useEffect, useRef } from 'react';

/**
 * Subscribe to a specific team event kind. The handler receives a strongly-typed payload matching
 * the `type` literal passed in. Registration is scoped to the calling component's mount — the
 * effect cleanup removes the listener on unmount or when either input changes.
 *
 * Handler identity does not need to be stable — the hook captures the latest handler via a ref
 * so callers can pass inline arrow functions without triggering listener churn.
 *
 * No-op when the provider isn't mounted (e.g. before login or on public routes).
 */
export const useTeamEvent = <T extends TeamEventType>(type: T, handler: (event: TeamEventOfType<T>) => void): void => {
  const context = useContext(TeamEventContext);

  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (context === null) {
      return;
    }
    const controller = new AbortController();
    context.subscribe(type, (event) => handlerRef.current(event), { signal: controller.signal });
    return () => {
      controller.abort();
    };
  }, [context, type]);
};
